import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS, LOGO_URL } from "@/lib/api";

const NAV_LINKS = [
  { label: "Home", href: "/#hero", testId: "nav-home" },
  { label: "About", href: "/#about", testId: "nav-about" },
  { label: "Services", href: "/#services", testId: "nav-services" },
  { label: "Gallery", href: "/#gallery", testId: "nav-gallery" },
  { label: "Reviews", href: "/#testimonials", testId: "nav-reviews" },
  { label: "Blog", href: "/blog", testId: "nav-blog" },
  { label: "Contact", href: "/#contact", testId: "nav-contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchorClick = (e, href) => {
    setOpen(false);
    // For same-page anchors, scroll smoothly and prevent navigation
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname === "/") {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
      // Else: allow default <Link> navigation to "/#hash", LandingPage will scroll on mount
    }
  };

  return (
    <nav
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_4px_20px_rgba(20,33,61,0.08)]"
          : "bg-white"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group" data-testid="nav-logo">
          <img
            src={LOGO_URL}
            alt="Corona Gas, HVAC & Mechanical"
            className="h-14 sm:h-16 w-auto rounded-lg shadow-sm group-hover:scale-[1.03] transition-transform duration-300"
          />
        </Link>

        {/* Desktop nav */}
        <ul className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <li key={link.label}>
              {link.href.startsWith("/#") ? (
                <Link
                  to={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className="px-4 py-2 text-[#0F0F0F] font-medium hover:text-[#E50914] transition-colors"
                  data-testid={link.testId}
                >
                  {link.label}
                </Link>
              ) : (
                <Link
                  to={link.href}
                  className="px-4 py-2 text-[#0F0F0F] font-medium hover:text-[#E50914] transition-colors"
                  data-testid={link.testId}
                >
                  {link.label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a href={BUSINESS.phoneHref} className="hidden xl:flex items-center gap-2 text-[#0F0F0F] font-semibold" data-testid="nav-phone">
            <Phone className="w-4 h-4 text-[#E50914]" />
            {BUSINESS.phone}
          </a>
          <Link
            to="/#contact"
            onClick={(e) => handleAnchorClick(e, "/#contact")}
            className="btn-coral"
            data-testid="nav-cta-contact"
          >
            Contact Us
          </Link>
        </div>

        {/* Mobile menu button */}
        <button
          className="lg:hidden text-[#0F0F0F] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-[#EAEAEA] bg-white" data-testid="mobile-menu">
          <ul className="px-4 py-4 space-y-1">
            {NAV_LINKS.map((link) => (
              <li key={link.label}>
                <Link
                  to={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className="block px-3 py-3 text-[#0F0F0F] font-medium rounded-lg hover:bg-[#FCE7E9] hover:text-[#E50914]"
                  data-testid={`mobile-${link.testId}`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/#contact"
                onClick={(e) => handleAnchorClick(e, "/#contact")}
                className="btn-coral w-full justify-center mt-2"
                data-testid="mobile-cta-contact"
              >
                Contact Us
              </Link>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
